import { dashboardErrorMessage } from '../../../../../lib/apiErrorMessages';
import { BrandCreationUncertainError } from '../../../../agency/demandApi';
import type { AssignedAgency } from '../../../../auth/agencyAccess';
import * as React from 'react';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { DiscardChangesDialog } from '../../../../../components/forms/DiscardChangesDialog';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { useBrandMutations, useCountriesQuery } from '../../hooks';
import type { BrandPayload, Brand } from '../../model';
import { BrandAgencyField } from './BrandAgencyField';
import {
  brandDialogDefaultValues,
  brandDialogResolver,
  brandLogoInput,
  buildBrandPayload,
} from './BrandDialog.helpers';
import { BrandLogoField } from './BrandLogoField';
import type { BrandDialogFormValue } from './BrandDialog.schema';

type BrandDialogProps = {
  brand: Brand | null;
  industries: string[];
  onClose: () => void;
  agency?: AssignedAgency | null | undefined;
  agencies?: AssignedAgency[];
  prepareAgency?: boolean;
  preview?: boolean;
  scoped?: boolean;
  onSave?: (agencyId: string, payload: BrandPayload, logo?: File | null) => Promise<unknown>;
};

export function BrandDialog({
  brand,
  industries,
  onClose,
  agency,
  prepareAgency = false,
  agencies = [],
  preview = false,
  scoped = false,
  onSave,
}: BrandDialogProps) {
  'use no memo';

  const countries = useCountriesQuery(!preview);
  const { create, update } = useBrandMutations();
  const form = useForm<BrandDialogFormValue>({
    defaultValues: brandDialogDefaultValues(brand, agency),
    resolver: brandDialogResolver(prepareAgency),
  });
  const [uncertain, setUncertain] = React.useState(false);
  const [discard, setDiscard] = React.useState(false);
  const { isDirty } = form.formState;
  const close = () => {
    if (!preview && !scoped && isDirty) setDiscard(true);
    else onClose();
  };
  const { errors } = useFormState({ control: form.control });
  const logo = useWatch({ control: form.control, name: 'logo' });
  const removeLogo = useWatch({ control: form.control, name: 'removeLogo' });
  const nameId = React.useId();
  const industryId = React.useId();
  const urlId = React.useId();
  const pending = create.isPending || update.isPending || form.formState.isSubmitting;
  const guard = useBusyDialogGuard(pending, close);
  const submitBlocked = preview || uncertain || (prepareAgency && !onSave);

  async function submit(values: BrandDialogFormValue) {
    if (submitBlocked) return;
    const payload = buildBrandPayload(values, brand);

    try {
      const logoInput = brandLogoInput(values.logo);
      if (onSave) await onSave(values.agencyId ?? '', payload, values.logo);
      else if (brand) await update.mutateAsync({ id: brand.id, payload, ...logoInput });
      else await create.mutateAsync({ payload, ...logoInput });
      toast.success(dashboardT(brand ? 'brands.updated' : 'brands.created'));
      onClose();
    } catch (error) {
      if (error instanceof BrandCreationUncertainError) setUncertain(true);
      form.setError('root.server', { message: dashboardErrorMessage(error, 'brands.saveError') });
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 overflow-visible p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {brand ? dashboardT('brands.form.editTitle') : dashboardT('brands.form.createTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('brands.form.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
              {prepareAgency || agency ? (
                <BrandAgencyField
                  control={form.control}
                  agency={agency}
                  agencies={agencies}
                  error={errors.agencyId?.message}
                />
              ) : null}
              <DashboardPageFormField
                label={dashboardT('brands.name')}
                required
                error={errors.name?.message}
                htmlFor={nameId}
              >
                <Input
                  id={nameId}
                  className="h-9"
                  required
                  aria-invalid={Boolean(errors.name)}
                  maxLength={200}
                  {...form.register('name')}
                />
              </DashboardPageFormField>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold" htmlFor={industryId}>
                    {dashboardT('brands.industry')} <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id={industryId}
                          className="h-9"
                          aria-label={dashboardT('brands.industry')}
                          aria-invalid={Boolean(errors.industry)}
                        >
                          <SelectValue placeholder={dashboardT('brands.form.selectIndustry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.industry?.message ? (
                    <p className="text-destructive text-xs">{errors.industry.message}</p>
                  ) : null}
                </div>
                <DashboardPageFormField
                  label={dashboardT('brands.url')}
                  error={errors.url?.message}
                  htmlFor={urlId}
                >
                  <Input
                    id={urlId}
                    className="h-9"
                    aria-invalid={Boolean(errors.url)}
                    {...form.register('url')}
                  />
                </DashboardPageFormField>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('brands.countries')}
                </Label>
                <Controller
                  control={form.control}
                  name="countryCodes"
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label={dashboardT('brands.countries')}
                      options={(preview ? [] : (countries.data ?? [])).map((country) => ({
                        id: country.code,
                        label: country.name,
                      }))}
                      value={field.value}
                      onValueChange={field.onChange}
                      loading={countries.isLoading}
                      placeholder={dashboardT('brands.form.selectCountries')}
                      emptyMessage={dashboardT('brands.form.noCountries')}
                    />
                  )}
                />
              </div>
              {countries.isError ? (
                <p role="alert" className="text-destructive text-xs">
                  {dashboardErrorMessage(countries.error)}
                </p>
              ) : null}
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold" htmlFor="brand-details">
                  {dashboardT('brands.details')}
                </Label>
                <Textarea id="brand-details" {...form.register('details')} />
              </div>
              <BrandLogoField
                existingLogoUrl={brand?.logoUrl ?? null}
                file={logo}
                removeExistingLogo={removeLogo}
                disabled={pending}
                onFileChange={(file) => {
                  form.setValue('logo', file, { shouldDirty: true });
                  form.setValue('removeLogo', false, { shouldDirty: true });
                }}
                onRemoveExistingLogo={() => {
                  form.setValue('logo', null, { shouldDirty: true });
                  form.setValue('removeLogo', true, { shouldDirty: true });
                }}
              />
              {errors.root?.server?.message ? (
                <p className="text-destructive text-sm" role="alert">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={close}>
                {dashboardT('brands.cancel')}
              </Button>
              <Button type="submit" disabled={pending || submitBlocked}>
                {pending ? dashboardT('brands.saving') : dashboardT('brands.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        <DiscardChangesDialog
          open={discard}
          onStay={() => {
            setDiscard(false);
          }}
          onDiscard={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
